// store.js
import { reactive } from 'vue'

export const store = reactive({
    currentUser: null,
    authUser: null,
    authSession: null,
    currentBrand: null,
    clientView: true,
    // shown on the login page when a client was signed out for having no
    // brand access left (see AuthService.initAuth)
    accessMessage: '',
    hasNewVersion: false,
    currentVersion: null,
    setCurrentUser(user) {
        store.currentUser = user
    },
    setAuthUser(user) {
        store.authUser = user
    },
    setAuthSession(session) {
        store.authSession = session
    },
    setCurrentBrand(brand) {
        store.currentBrand = brand
    },
    setClientView(clientView) {
        store.clientView = clientView;
    },
    setHasNewVersion(value) {
        store.hasNewVersion = value;
    },
    setCurrentVersion(version) {
        store.currentVersion = version;
    }
});

export const campaign = reactive({
    data_loaded: false,
    campaign: {},
    currentBrand: {},
    currentUser: {},
    invitations: [],
    invitations_count: 0,
    posts_count: 0,
    posts: [],
    performance: {},
});

export const resetCampaign = () => {
    campaign.data_loaded = false;
    campaign.campaign = {};
    campaign.currentBrand = {};
    campaign.currentUser = {};
    campaign.invitations = [];
    campaign.invitations_count = 0;
    campaign.posts_count = 0;
    campaign.posts = [];
    campaign.performance = {};
}

export const managementUserIds = [
    '229781b5-59d3-4630-a497-665db935f505', // Nadine
    'c666dc32-ec7a-443c-80ca-dce5f1b4c755', // Yana
    'ebb4ff71-3a5d-4f15-b7bc-84ed931feb80', // Nadia
    '527f61cf-3794-4508-aebd-3527cb7aeef9', // Tala G
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', //Tanaz
    '4a2e39ec-5568-4f94-b728-43b02b1610d8', // Nour Nawfal
    '2b6ef67a-2fdb-4ef3-b022-836aeb69cb7c' //Rayane
]
// Extra users allowed to set Management Approval on invitations, IN ADDITION to
// managementUserIds. Kept separate so it doesn't widen managementUserIds usage
// anywhere else.
export const managementApprovalUserIds = [
    'a0321d70-5876-43dc-84b1-311d1d3380aa', // Teresa Dargham
    'ee19b8ab-0524-4c87-9445-d09760012c13', // Saad Edrees
    'd29a11de-9e98-4439-8022-72ca5fc50632' // Tima Kalakech
]
export const higherManagementUserIds = [
    '229781b5-59d3-4630-a497-665db935f505', // Nadine
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', //Tanaz
    'c691d1bd-328c-43cd-a51c-f3f833997fce', // Mikella
    '2b6ef67a-2fdb-4ef3-b022-836aeb69cb7c', // Rayane
    '4a2e39ec-5568-4f94-b728-43b02b1610d8', // Nour Nawfal
    'e8973bf6-6c37-4757-a154-2f1f16a3e982', // Mitzi Catherine
    '66fbae71-969a-4f0e-97e7-298c035a97fe', // Ekaterina Al Olaby
    'b55c91a0-f7bd-47a3-af42-7d5058d19ee6' // Amer Ghouleh
]

// Operations team: on campaigns using the new finance formula (v2) they edit
// the Operations Margin % and the logistics / additional-services actual spend
// on the Finance tab, without the rest of the Finance edit rights. Tanaz's
// sheet names Mikaella, Katya and Sade. Higher management keep their access.
export const operationsUserIds = [
    'c691d1bd-328c-43cd-a51c-f3f833997fce', // Mikella
    '66fbae71-969a-4f0e-97e7-298c035a97fe', // Ekaterina (Katya)
    // TODO Sade — add her user id
]

// Who may switch a campaign between the current and the new finance formula
// (campaign.finance_model) while the new formula is being tested.
export const financeModelSwitchUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '229781b5-59d3-4630-a497-665db935f505', // Nadine
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', // Tanaz
]

// The only people who can open the Performance dashboards (team overview and
// individual boards). Deliberately narrower than higherManagementUserIds.
export const performanceUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', // Tanaz
    '229781b5-59d3-4630-a497-665db935f505', // Nadine
    '4a2e39ec-5568-4f94-b728-43b02b1610d8' // Nour Nawfal
]

// Client review links (brief / samples / selection / script / content) are
// rolled out to the whole team (Oct 2026). Who can generate a link for a given
// campaign is still decided per column (CM / casting / higher management);
// this switch only hides the feature as a whole. Set it back to a user-id
// check to restrict it again.
export const canUseClientReview = () => !!store.currentUser?.id

// "Download deck" on the client review link (samples / selection) is in testing:
// only these users see it, and only while signed in to the app in the same
// browser. Clients never see it. Widen this list to roll it out.
export const talentDeckUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776' // Tanaz
]
export const canDownloadTalentDeck = () => talentDeckUserIds.includes(store.currentUser?.id)
// "Include fees" on selection client links: same two people for now. Everyone else
// can still create and share selection links, they just can't switch fees on or off.
export const feeLinkUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776' // Tanaz
]
export const canIncludeFees = () => feeLinkUserIds.includes(store.currentUser?.id)
// "Client status link" (campaign status page for the client) is in testing with the
// same two people. Widen this list, or return canUseClientReview(), to roll it out.
export const statusLinkUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776' // Tanaz
]
export const canUseStatusLink = () => statusLinkUserIds.includes(store.currentUser?.id)

// "Draft rationale" button on the invitation dialog: open to all staff.
// (Trial allowlist kept below in case it needs to be narrowed again.)
export const rationaleDraftUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', // Tanaz
    '2b6ef67a-2fdb-4ef3-b022-836aeb69cb7c' // Rayane
]
export const canDraftRationale = () => !!store.currentUser?.staff

// The only people who see the workload meter (and its breakdown) on the
// Performance individual boards. Narrower than performanceUserIds.
export const performanceWorkloadUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105' // Ahmer
]

// Who sees the Budget card and Budget column on the Performance individual
// boards. Narrower than performanceUserIds: someone can have the dashboards
// without the money.
export const performanceBudgetUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', // Tanaz
    '229781b5-59d3-4630-a497-665db935f505' // Nadine
]

// Leave management is in development / testing: only these people can open it.
// The database enforces the same list (public.leave_admin_ids() in the
// leave_management migration). At launch, open canUseLeave to every staff
// member and change leave_can_use() in SQL to match; isLeaveAdmin stays HR-only.
export const leaveUserIds = [
    'fe31cc65-3f7f-4345-8b21-95bf2eff3105', // Ahmer
    '725266fb-18c3-4acd-9632-b3a8c0ab5776', // Tanaz
    'b55c91a0-f7bd-47a3-af42-7d5058d19ee6' // Amer Ghouleh
]
export const canUseLeave = () => leaveUserIds.includes(store.currentUser?.id)
export const isLeaveAdmin = () => leaveUserIds.includes(store.currentUser?.id)

// Extra users allowed to toggle "Show price even without BR%" on the campaign
// price breakdown, IN ADDITION to higherManagementUserIds. Kept separate so it
// does not widen higher-management rights anywhere else.
export const priceWithoutBrUserIds = [
    'c666dc32-ec7a-443c-80ca-dce5f1b4c755' // Yana
]

// Sort staff option lists by display name. localeCompare so accented names
// land where a reader expects, and numeric:true so any trailing digits sort
// 2 before 10. Falls back to email when a name is missing, so an incomplete
// record does not jump to the top of every dropdown.
export const staffDisplayName = (emp) => {
    const u = (emp && emp.users) || {};
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return name || u.email || '';
};
export const sortStaffByName = (list) =>
    [...(Array.isArray(list) ? list : [])].sort((a, b) =>
        staffDisplayName(a).localeCompare(staffDisplayName(b), undefined, {
            sensitivity: 'base',
            numeric: true,
        })
    );

// Is this user on the casting side of the campaign — either the primary casting
// owner (the derived `casting` scalar) or one of its collaborators?
//
// Collaborators are extra casting people stored in team.casting with
// is_collaborator = true. They are deliberately NOT task owners: overdue casting
// items stay with the primary in the report / My Tasks / workload. This check
// governs EDIT RIGHTS only, so both can work the campaign.
// POLICY: who may edit the casting-side tracker columns (Financials, Talent
// Samples / Selection, Required Influencers, Contracts, Client Protocols
// (Casting), Impact Sheet (Casting), CIQ, Content board).
//
// Casting is READ-ONLY on the tracker except Notes, the Handover checklist and
// Talent Contracts (TalentContractsColumn adds casting back for that one).
// The campaign's Campaign Manager edits these columns on casting's behalf, and
// higher management can edit anything. Casting still OWNS the tasks — overdue
// items in the reports and My Tasks stay attributed to the casting owner —
// they just can't change the cells. Collaborators follow the same rule as the
// primary casting owner.
//
// Every casting-side column goes through this one function so the policy can
// be changed (or reverted) in one place.
export const canEditCastingColumn = (campaign, userId) => {
    if (!userId) return false;
    return isCmUser(campaign, userId) || higherManagementUserIds.includes(userId);
};

export const isCastingUser = (campaign, userId) =>
    isRoleUser(campaign, userId, 'casting');

// Same for the Campaign Manager side.
export const isCmUser = (campaign, userId) =>
    isRoleUser(campaign, userId, 'cm');

const isRoleUser = (campaign, userId, role) => {
    if (!userId || !campaign) return false;
    const isCasting = role === 'casting';
    const primary = isCasting ? campaign.casting : campaign.campaign_manager;
    if (primary === userId) return true;
    const collaborators = isCasting
        ? campaign.casting_collaborators
        : campaign.campaign_manager_collaborators;
    if (Array.isArray(collaborators) && collaborators.includes(userId)) return true;
    // Fall back to the raw team array when the view column isn't present
    // (e.g. a row merged optimistically from a realtime payload).
    const teamKey = isCasting ? 'casting' : 'campaign_manager';
    return (campaign.team?.[teamKey] || []).some(
        (m) => m && m.is_collaborator && m.id === userId
    );
}

export const savingTracker = reactive({
    saving: false
})
